import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Users, MapPin, Check, ChevronDown, ChevronUp } from "lucide-react";

export default function AreaTeamAssignment({ eventId, isOpen, onClose }) {
  const [areas, setAreas] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedArea, setExpandedArea] = useState(null);
  const [assignments, setAssignments] = useState({}); // { area_id: [team_id1, team_id2] }
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (isOpen && eventId) {
      loadData();
    }
  }, [isOpen, eventId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [areasRes, teamsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/areas-with-teams`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/sweeper-teams`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAreas(areasRes.data);
      setTeams(teamsRes.data);
      
      // Initialiser assignments fra eksisterende data
      const initialAssignments = {};
      areasRes.data.forEach(area => {
        initialAssignments[area.id] = area.assigned_team_ids || [];
      });
      setAssignments(initialAssignments);
    } catch (error) {
      console.error("Kunne ikke hente data:", error);
      toast.error("Kunne ikke hente områder og hold");
    } finally {
      setLoading(false);
    }
  };

  const toggleTeamAssignment = (areaId, teamId) => {
    setAssignments(prev => {
      const current = prev[areaId] || [];
      if (current.includes(teamId)) {
        return { ...prev, [areaId]: current.filter(id => id !== teamId) };
      } else {
        return { ...prev, [areaId]: [...current, teamId] };
      }
    });
  };

  const saveAssignment = async (areaId) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/areas/${areaId}/assign-teams`,
        assignments[areaId] || [],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Hold tildelt til område!");
    } catch (error) {
      console.error("Fejl ved tildeling:", error);
      toast.error("Kunne ikke tildele hold");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-zinc-700 rounded-t-xl sm:rounded-lg shadow-2xl w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Users className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-mono uppercase">Hold-tildeling</h2>
              <p className="text-xs text-muted-foreground">Tildel sweeper-hold til områder</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          ) : areas.length === 0 ? (
            <div className="text-center py-12 text-zinc-400">
              <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Ingen områder fundet</p>
              <p className="text-xs mt-1">Opret områder på kortet først</p>
            </div>
          ) : (
            <div className="space-y-3">
              {areas.map(area => (
                <div 
                  key={area.id} 
                  className="border border-zinc-700 rounded-lg overflow-hidden"
                >
                  {/* Area header */}
                  <button
                    onClick={() => setExpandedArea(expandedArea === area.id ? null : area.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-zinc-800 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: area.color || '#8b5cf6' }}
                      />
                      <div>
                        <p className="font-bold text-white">{area.name}</p>
                        <p className="text-xs text-zinc-400">
                          {(assignments[area.id] || []).length} hold tildelt
                        </p>
                      </div>
                    </div>
                    {expandedArea === area.id ? (
                      <ChevronUp className="h-5 w-5 text-zinc-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-zinc-400" />
                    )}
                  </button>

                  {/* Team selection */}
                  {expandedArea === area.id && (
                    <div className="border-t border-zinc-700 p-4 bg-zinc-800/50">
                      {teams.length === 0 ? (
                        <p className="text-sm text-zinc-400 text-center py-4">
                          Ingen hold oprettet
                        </p>
                      ) : (
                        <>
                          <p className="text-xs text-zinc-400 mb-3">Vælg hold til dette område:</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {teams.map(team => {
                              const isAssigned = (assignments[area.id] || []).includes(team.id);
                              return (
                                <button
                                  key={team.id}
                                  onClick={() => toggleTeamAssignment(area.id, team.id)}
                                  className={`flex items-center gap-2 p-2 rounded border transition-all ${
                                    isAssigned 
                                      ? 'border-green-500 bg-green-500/20 text-green-400' 
                                      : 'border-zinc-600 hover:border-green-500/50 text-white'
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded flex items-center justify-center ${
                                    isAssigned ? 'bg-green-500' : 'bg-zinc-700'
                                  }`}>
                                    {isAssigned && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <span className="text-sm font-mono">{team.name}</span>
                                </button>
                              );
                            })}
                          </div>
                          <div className="mt-4 flex justify-end">
                            <Button
                              size="sm"
                              onClick={() => saveAssignment(area.id)}
                              disabled={saving}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {saving ? 'Gemmer...' : 'Gem tildeling'}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-700 p-4 flex justify-between items-center">
          <p className="text-xs text-zinc-400">
            {areas.length} områder • {teams.length} hold
          </p>
          <Button variant="outline" onClick={onClose} className="border-zinc-600 hover:bg-zinc-800">
            Luk
          </Button>
        </div>
      </div>
    </div>
  );
}
